import {
  Download as DownloadIcon,
  HelpOutline as HelpOutlineIcon,
  PictureAsPdf as PictureAsPdfIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { pdf } from "@react-pdf/renderer";
import QRCode from "qrcode";
import { useCallback, useEffect, useRef, useState } from "react";
import CDNSLAMDocument from "../../components/CDNSLAMDocument";
import cdnslamService from "../../services/cdnslam";
import empreendimentoService from "../../services/empreendimento";
import pdfLayoutService from "../../services/pdfLayout";
import { ATIVIDADE_EMPREENDIMENTO_LABELS, ESTADOS_BRASIL } from "../../utils/constants";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";

const NOME_DECLARACAO = "Certidão Declaratória de Não Sujeição ao Licenciamento Ambiental";
const ATIVIDADES_ELEGIVEIS_CDNSLAM = [
  "03.27 - Coleta, Transporte e Armazenamento de Resíduos Sólidos e Produtos",
  "09.01 - Linhas de Distribuição até 15 kV",
  "09.02 - Linhas de Distribuição > 15 kV e ≤ 138 kV",
  "26.08 - Vias - Manutenção e Restauração",
  "01.07 - Projetos de Irrigação (com uso de agrotóxico)",
  "01.08 - Projetos de Irrigação (sem uso de agrotóxico)",
  "01.01 - Criação de animais sem abate (Avicultura)",
  "01.01 - Criação de animais sem abate (Suinocultura)",
  "01.01 - Criação de animais sem abate (Bovinocultura e Bubalinocultura)",
  "01.01 - Criação de animais sem abate (Ovinocaprinocultura)",
  "01.02 - Cultivo de plantas medicinais, aromáticas e condimentares",
];

function CdnslamPage() {
  const [tab, setTab] = useState(0);

  // --- Aba "Minhas Declarações" ---
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [orderBy, setOrderBy] = useState("dataSolicitacao");
  const [orderDirection, setOrderDirection] = useState("desc");
  const [filtroNome, setFiltroNome] = useState("");
  const [loadingLista, setLoadingLista] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const debounceRef = useRef(null);

  // --- Aba "Nova Declaração" ---
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [empreendimentoSelecionado, setEmpreendimentoSelecionado] = useState(null);
  const [elegivel, setElegivel] = useState(null);
  const [cdnslamExistente, setCdnslamExistente] = useState(null);
  const [verificando, setVerificando] = useState(false);
  const [emitindo, setEmitindo] = useState(false);
  const [confirmacaoAberta, setConfirmacaoAberta] = useState(false);

  const [headerImgSrc, setHeaderImgSrc] = useState(null);
  const [footerImgSrc, setFooterImgSrc] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [infoModalAberto, setInfoModalAberto] = useState(false);
  const [atividadesElegiveis, setAtividadesElegiveis] = useState([]);
  const [loadingAtividades, setLoadingAtividades] = useState(false);

  const handleAbrirInfoModal = async () => {
    setInfoModalAberto(true);
    if (atividadesElegiveis.length > 0) return;
    setLoadingAtividades(true);
    try {
      const resp = await cdnslamService.obterAtividadesElegiveis();
      setAtividadesElegiveis(resp?.data ?? []);
    } catch {
      setAtividadesElegiveis(
        ATIVIDADES_ELEGIVEIS_CDNSLAM.map((label, index) => ({
          value: `fallback-${index}`,
          label,
        }))
      );
    } finally {
      setLoadingAtividades(false);
    }
  };

  const carregarLista = useCallback(async (pg, ps, nome, ob, od) => {
    setLoadingLista(true);
    try {
      const result = await cdnslamService.listarMinhas(pg, ps, nome, ob, od);
      setRows(result?.data?.data ?? []);
      setTotalCount(result?.data?.totalCount ?? 0);
      setTotalPaginas(result?.data?.totalPages ?? 0);
    } catch {
      setRows([]);
      setTotalCount(0);
      setTotalPaginas(0);
    } finally {
      setLoadingLista(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 0) {
      carregarLista(pageNumber, pageSize, filtroNome, orderBy, orderDirection);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, pageNumber, pageSize, orderBy, orderDirection]);

  const handleFiltroNomeChange = (e) => {
    const value = e.target.value;
    setFiltroNome(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPageNumber(1);
      carregarLista(1, pageSize, value, orderBy, orderDirection);
    }, 400);
  };

  const handleSort = (coluna) => {
    const isAsc = orderBy === coluna && orderDirection === "asc";
    const novaDir = isAsc ? "desc" : "asc";
    setOrderBy(coluna);
    setOrderDirection(novaDir);
    setPageNumber(1);
  };

  const handleDownloadRow = async (id) => {
    setDownloadingId(id);
    try {
      const response = await cdnslamService.obterPorId(id);
      const cdnslam = response?.data;
      if (cdnslam?.fileContent) {
        const byteArray = Uint8Array.from(atob(cdnslam.fileContent), (c) => c.charCodeAt(0));
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = cdnslam.fileName || "CDNSLAM.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        setSnackbar({ open: true, message: "Arquivo não encontrado.", severity: "warning" });
      }
    } catch {
      setSnackbar({ open: true, message: "Erro ao baixar CDNSLAM.", severity: "error" });
    } finally {
      setDownloadingId(null);
    }
  };

  // Carregar empreendimentos para a aba Nova Declaração
  useEffect(() => {
    if (tab === 1 && empreendimentos.length === 0) {
      empreendimentoService.listar().then(setEmpreendimentos).catch(() => setEmpreendimentos([]));
    }
  }, [tab, empreendimentos.length]);

  const carregarPdfLayout = useCallback(async () => {
    try {
      const res = await pdfLayoutService.obterLatest();
      const layoutId = res.data?.data;
      if (!layoutId) {
        setHeaderImgSrc(null);
        setFooterImgSrc(null);
        return;
      }

      const toDataUrl = (blob) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });

      const [headerRes, footerRes] = await Promise.all([
        pdfLayoutService.getHeaderImage(layoutId).catch(() => null),
        pdfLayoutService.getFooterImage(layoutId).catch(() => null),
      ]);

      setHeaderImgSrc(headerRes ? await toDataUrl(headerRes.data) : null);
      setFooterImgSrc(footerRes ? await toDataUrl(footerRes.data) : null);
    } catch {
      setHeaderImgSrc(null);
      setFooterImgSrc(null);
    }
  }, []);

  useEffect(() => {
    if (tab === 1) {
      carregarPdfLayout();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleSelecionarEmpreendimento = async (emp) => {
    setEmpreendimentoSelecionado(emp);
    setElegivel(null);
    setCdnslamExistente(null);
    if (!emp) return;

    setVerificando(true);
    try {
      const [elegibilidadeResp, cdnslamResp] = await Promise.all([
        empreendimentoService
          .verificarElegibilidadeCertidaoAutomatica(emp.id)
          .catch(() => ({ elegivel: false })),
        cdnslamService
          .obterPorEmpreendimentoId(emp.id)
          .catch(() => ({ data: null })),
      ]);
      setElegivel(elegibilidadeResp.elegivel === true);
      setCdnslamExistente(cdnslamResp?.data ?? null);
    } finally {
      setVerificando(false);
    }
  };

  const getNomeEstado = (sigla) => {
    const estado = ESTADOS_BRASIL.find((e) => e.sigla === sigla);
    return estado ? estado.nome : sigla;
  };

  const buildEnderecoCompleto = (empreendimento) => {
    const enderecoSource = empreendimento?.enderecoAtividade || empreendimento?.endereco;
    if (!enderecoSource) return "Não informado";
    const e = enderecoSource;
    const rua = e.rua || "";
    const numero = e.numero ? `, nº ${e.numero}` : "";
    const municipio = e.municipio || "";
    const estado = getNomeEstado(e.estado);
    const cep = e.cep ? ` - CEP: ${e.cep}` : "";
    return `${rua}${numero}, ${municipio} - ${estado}${cep}`;
  };

  const handleEmitirCDNSLAM = async () => {
    if (!empreendimentoSelecionado) return;

    setEmitindo(true);
    try {
      const [dadosCompletos, proximoNumeroResp] = await Promise.all([
        empreendimentoService.obter(empreendimentoSelecionado.id),
        cdnslamService.obterProximoNumero(),
      ]);
      const proximoNumero = proximoNumeroResp?.data;
      if (!proximoNumero?.numeroProcesso || !proximoNumero?.numeroInformado) {
        throw new Error("Não foi possível obter o próximo número de processo.");
      }

      const atividadeLabel =
        ATIVIDADE_EMPREENDIMENTO_LABELS[dadosCompletos.atividadeEmpreendimento] || "Não informado";
      const enderecoCompleto = buildEnderecoCompleto(dadosCompletos);

      const qrUrl = `${window.location.origin}/validar/cdnslam/${empreendimentoSelecionado.id}`;
      const qrCodeDataUrl = await QRCode.toDataURL(qrUrl, { margin: 1 });

      const doc = (
        <CDNSLAMDocument
          empreendimento={dadosCompletos}
          atividadeLabel={atividadeLabel}
          atividadeEmpreendimento={dadosCompletos.atividadeEmpreendimento}
          endereco={enderecoCompleto}
          headerImgSrc={headerImgSrc}
          footerImgSrc={footerImgSrc}
          qrCodeDataUrl={qrCodeDataUrl}
          numeroProcesso={proximoNumero.numeroProcesso}
        />
      );
      const blob = await pdf(doc).toBlob();
      await cdnslamService.criar(
        empreendimentoSelecionado.id,
        blob,
        proximoNumero.numeroInformado,
      );

      setSnackbar({ open: true, message: "CDNSLAM emitida com sucesso!", severity: "success" });
      setElegivel(null);
      setCdnslamExistente(null);
      setEmpreendimentoSelecionado(null);
      setTab(0);
      setPageNumber(1);
      carregarLista(1, pageSize, filtroNome, orderBy, orderDirection);
    } catch (err) {
      const msg = getApiErrorMessage(err, "Erro ao emitir CDNSLAM.");
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setEmitindo(false);
    }
  };

  const handleDownloadCDNSLAM = async () => {
    if (!empreendimentoSelecionado) return;
    try {
      const response = await cdnslamService.obterPorEmpreendimentoId(empreendimentoSelecionado.id);
      const cdnslam = response?.data;
      if (cdnslam?.fileContent) {
        const byteArray = Uint8Array.from(atob(cdnslam.fileContent), (c) => c.charCodeAt(0));
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = cdnslam.fileName || "CDNSLAM.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        setSnackbar({ open: true, message: "Arquivo da CDNSLAM não encontrado.", severity: "warning" });
      }
    } catch {
      setSnackbar({ open: true, message: "Erro ao baixar CDNSLAM.", severity: "error" });
    }
  };

  return (
    <Container sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2.125rem" } }}
          >
            CDNSLAM
          </Typography>
          <IconButton
            onClick={handleAbrirInfoModal}
            aria-label="O que é CDNSLAM? Quais atividades são elegíveis?"
            size="large"
            title="Atividades elegíveis para CDNSLAM"
            sx={{
              color: "success.main",
              alignSelf: { xs: "flex-start", sm: "center" },
              "&:hover": { bgcolor: "success.light", color: "success.dark", opacity: 0.85 },
            }}
          >
            <HelpOutlineIcon fontSize="large" />
          </IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, sm: 3 } }}>
          {NOME_DECLARACAO}
        </Typography>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3 }}
        >
          <Tab label="Minhas Declarações" />
          <Tab label="Nova Declaração" />
        </Tabs>

        {tab === 0 && (
          <Box>
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Filtrar por Empreendimento"
                    value={filtroNome}
                    onChange={handleFiltroNomeChange}
                  />
                </Grid>
              </Grid>
            </Paper>

            <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={orderBy === "nomeDeclaracao" ? orderDirection : false}>
                      <TableSortLabel
                        active={orderBy === "nomeDeclaracao"}
                        direction={orderBy === "nomeDeclaracao" ? orderDirection : "asc"}
                        onClick={() => handleSort("nomeDeclaracao")}
                      >
                        Declaração
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy === "nomeEmpreendimento" ? orderDirection : false}>
                      <TableSortLabel
                        active={orderBy === "nomeEmpreendimento"}
                        direction={orderBy === "nomeEmpreendimento" ? orderDirection : "asc"}
                        onClick={() => handleSort("nomeEmpreendimento")}
                      >
                        Empreendimento
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy === "dataSolicitacao" ? orderDirection : false}>
                      <TableSortLabel
                        active={orderBy === "dataSolicitacao"}
                        direction={orderBy === "dataSolicitacao" ? orderDirection : "asc"}
                        onClick={() => handleSort("dataSolicitacao")}
                      >
                        Data de Emissão
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Opções</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingLista ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        Nenhum registro encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>CDNSLAM</TableCell>
                        <TableCell>{row.empreendimentoNome || "—"}</TableCell>
                        <TableCell>
                          {row.createAt
                            ? new Date(row.createAt).toLocaleString("pt-BR")
                            : "—"}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            title="Baixar CDNSLAM"
                            aria-label="Baixar CDNSLAM"
                            disabled={!!downloadingId}
                            onClick={() => handleDownloadRow(row.id)}
                          >
                            {downloadingId === row.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DownloadIcon />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={Math.max(0, pageNumber - 1)}
              onPageChange={(_, newPage) => setPageNumber(newPage + 1)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPageNumber(1);
              }}
              rowsPerPageOptions={[5, 10, 20, 50]}
              labelRowsPerPage="Itens por página"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}${totalPaginas > 0 ? ` | Página ${pageNumber} de ${totalPaginas}` : ""}`
              }
            />
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Selecione o Empreendimento
              </Typography>
              <Autocomplete
                options={empreendimentos}
                getOptionLabel={(opt) => opt.nome || ""}
                value={empreendimentoSelecionado}
                onChange={(_, value) => handleSelecionarEmpreendimento(value)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="Empreendimento" />
                )}
                sx={{ maxWidth: { xs: "100%", sm: 500 } }}
              />
            </Paper>

            {verificando && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Verificando elegibilidade...</Typography>
              </Box>
            )}

            {!verificando && elegivel === false && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Este empreendimento não é elegível para emissão de CDNSLAM. Atividades elegíveis:
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                  {ATIVIDADES_ELEGIVEIS_CDNSLAM.map((atividade) => (
                    <Box component="li" key={atividade}>
                      {atividade}
                    </Box>
                  ))}
                </Box>
              </Alert>
            )}

            {!verificando && elegivel === true && cdnslamExistente && (
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Já existe uma CDNSLAM emitida para este empreendimento. Você pode baixar a
                  CDNSLAM atual ou emitir uma nova, o que <strong>invalidará e excluirá
                  automaticamente a anterior</strong>.
                </Alert>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadCDNSLAM}
                    fullWidth
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    Baixar CDNSLAM atual
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={() => setConfirmacaoAberta(true)}
                    disabled={emitindo}
                    fullWidth
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    Emitir nova CDNSLAM
                  </Button>
                </Stack>
              </Paper>
            )}

            {!verificando && elegivel === true && !cdnslamExistente && (
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Este empreendimento está apto para emissão de CDNSLAM. O número do processo
                  será gerado automaticamente.
                </Alert>
                <Button
                  variant="contained"
                  startIcon={emitindo ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
                  onClick={handleEmitirCDNSLAM}
                  disabled={emitindo}
                  fullWidth
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {emitindo ? "Emitindo..." : "Emitir CDNSLAM"}
                </Button>
              </Paper>
            )}
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Modal: Atividades elegíveis para CDNSLAM */}
        <Dialog
          open={infoModalAberto}
          onClose={() => setInfoModalAberto(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ pb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  bgcolor: "success.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <HelpOutlineIcon sx={{ color: "#fff", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600} lineHeight={1.3}>
                  Atividades sujeitas à CDNSLAM
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Certidão Declaratória de Não Sujeição ao Licenciamento Ambiental Municipal
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 0 }}>
            <Box sx={{ px: 3, py: 2, bgcolor: "success.50", borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography variant="body2" color="text.secondary">
                Empreendimentos enquadrados em uma dessas atividades podem emitir a certidão{" "}
                <strong>diretamente pelo sistema</strong>, sem necessidade de análise manual.
              </Typography>
            </Box>

            {loadingAtividades ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} color="success" />
              </Box>
            ) : atividadesElegiveis.length === 0 ? (
              <Box sx={{ px: 3, py: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhuma atividade encontrada.
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {atividadesElegiveis.map((a, index) => (
                  <ListItem
                    key={`${a.value}-${index}`}
                    divider={index < atividadesElegiveis.length - 1}
                    sx={{ px: 3, py: 1.25 }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "success.main",
                        flexShrink: 0,
                        mr: 1.5,
                        mt: 0.25,
                        alignSelf: "flex-start",
                      }}
                    />
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500}>
                          {a.label}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => setInfoModalAberto(false)}
              variant="contained"
              color="success"
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo de confirmação de substituição */}
        <Dialog open={confirmacaoAberta} onClose={() => setConfirmacaoAberta(false)}>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberIcon color="warning" />
            Substituir CDNSLAM existente
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Ao emitir uma nova CDNSLAM, a certidão atual deste empreendimento será
              <strong> invalidada e excluída permanentemente</strong>. Essa ação não pode ser desfeita.
              <br /><br />
              Deseja continuar e emitir uma nova CDNSLAM?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmacaoAberta(false)}>Cancelar</Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={emitindo ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
              disabled={emitindo}
              onClick={() => {
                setConfirmacaoAberta(false);
                handleEmitirCDNSLAM();
              }}
            >
              Sim, emitir nova
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}

export default CdnslamPage;
